<template>
    <div class="space-y-6">
        <div class="flex items-center justify-between">
            <h2 class="text-heading-md">Text Input Examples</h2>
            <div class="flex items-center gap-4">
                <span class="text-content-md font-medium text-content-secondary">Size:</span>
                <DsRadio
                    v-model="size"
                    label="Medium (default)"
                    name="text-input-size"
                    value="md"
                />
                <DsRadio
                    v-model="size"
                    label="Small"
                    name="text-input-size"
                    value="sm"
                />
            </div>
        </div>

        <h3 class="mb-4 border-b border-ds-primary-200 pb-2 text-heading-sm">Text Input</h3>

        <div class="grid grid-cols-2 gap-4">
            <div>
                <h5 class="mb-3 text-content-md font-semibold">Basic States</h5>
                <div class="space-y-4">
                    <DsTextInput
                        v-model="basicValue"
                        helperText=""
                        label="Full Name"
                        labelTooltip="This is a tooltip"
                        :size
                        type="text"
                    />

                    <DsTextInput
                        v-model="helperValue"
                        helperText="With helper text"
                        label="Full Name"
                        labelTooltip="This is a tooltip"
                        :size
                        type="text"
                    />

                    <DsTextInput
                        v-model="customTooltipValue"
                        helperText="With custom tooltip content"
                        label="Full Name"
                        :size
                        type="text"
                    >
                        <template #labelTooltip>
                            <div>
                                Custom tooltip with
                                <strong>rich content</strong>
                            </div>
                        </template>
                    </DsTextInput>

                    <DsTextInput
                        v-model="requiredValue"
                        helperText="Required field"
                        label="Full Name"
                        required
                        :size
                        type="text"
                    />

                    <DsTextInput
                        v-model="disabledValue"
                        disabled
                        helperText="Disabled field"
                        label="Full Name"
                        :size
                        type="text"
                    />

                    <DsTextInput
                        v-model="readonlyValue"
                        helperText="Readonly field"
                        label="Full Name"
                        readonly
                        :size
                        type="text"
                    />

                    <DsTextInput
                        v-model="labelSlotValue"
                        helperText="Custom label slot"
                        label="Full Name"
                        :size
                        type="text"
                    >
                        <template #label>
                            <span>
                                Full Name
                                <strong class="text-content-primary">(custom)</strong>
                            </span>
                        </template>
                    </DsTextInput>

                    <DsTextInput
                        v-model="maxlengthValue"
                        helperText="Max 10 characters"
                        label="Full Name"
                        :maxlength="10"
                        :size
                        type="text"
                    />
                </div>
            </div>

            <div>
                <h5 class="mb-3 text-content-md font-semibold">Error, Warning & Slots</h5>
                <div class="space-y-4">
                    <DsTextInput
                        v-model="errorValue"
                        error="Error message"
                        helperText="Field with error"
                        label="Full Name"
                        :size
                        type="text"
                    />

                    <DsTextInput
                        v-model="errorBooleanValue"
                        :error="true"
                        helperText="Field with error"
                        label="Full Name"
                        :size
                        type="text"
                    />

                    <DsTextInput
                        v-model="warningValue"
                        label="Full Name"
                        :size
                        type="text"
                        warning="Warning message"
                    />

                    <DsTextInput
                        v-model="errorWinsValue"
                        error="Error wins over warning"
                        label="Full Name"
                        :size
                        type="text"
                        warning="This warning is suppressed"
                    />

                    <DsTextInput
                        v-model="slotsValue"
                        helperText="Input with prefix and suffix slots"
                        label="Full Name"
                        :size
                        type="text"
                    >
                        <template #leading>
                            <DsIcon
                                icon="star"
                                size="sm"
                            />
                        </template>
                        <template #trailing>
                            <DsIcon
                                icon="star"
                                size="sm"
                            />
                        </template>
                    </DsTextInput>

                    <DsTextInput
                        v-model="helperSlotValue"
                        helper-text="Input with helper text slot"
                        label="Full Name"
                        :size
                        type="text"
                    >
                        <template #helperText> Custom helper text content goes here </template>
                    </DsTextInput>
                </div>
            </div>
        </div>

        <h3 class="mb-4 border-b border-ds-primary-200 pb-2 text-heading-sm">Different Input Types</h3>

        <div class="grid grid-cols-3 gap-4">
            <DsTextInput
                v-model="emailValue"
                helperText="Enter your email"
                label="Email Address"
                :size
                type="email"
            />

            <DsTextInput
                v-model="passwordValue"
                helperText="Enter your password"
                label="Password"
                :size
                type="password"
            />

            <DsTextInput
                v-model="numberValue"
                helperText="Enter your age"
                label="Age"
                :size
                type="number"
            />
        </div>

        <h3 class="mb-4 border-b border-ds-primary-200 pb-2 text-heading-sm">With Mask</h3>

        <div class="grid grid-cols-3 gap-4">
            <DsTextInputMasked
                v-model="ssnValue"
                helperText="Enter your social security number"
                label="Social security number"
                :mask="MaskType.SSN"
                :placeholder="MaskPlaceholder.SSN"
                :size
                type="text"
            />

            <DsTextInputMasked
                v-model="einValue"
                helperText="Enter your EIN"
                label="EIN"
                :mask="MaskType.EIN"
                :placeholder="MaskPlaceholder.EIN"
                :size
                type="text"
            />

            <DsTextInputMasked
                v-model="accountValue"
                helperText="Enter your account number"
                label="Account Number"
                :mask="MaskType.ACCOUNT_NUMBER"
                :size
                type="text"
            />

            <DsTextInputMasked
                v-model="ssnDisabledValue"
                disabled
                helperText="Disabled field"
                label="Social security number"
                :mask="MaskType.SSN"
                :placeholder="MaskPlaceholder.SSN"
                :size
                type="text"
            />

            <DsTextInputMasked
                v-model="ssnReadonlyValue"
                helperText="Readonly field"
                label="Social security number"
                :mask="MaskType.SSN"
                :placeholder="MaskPlaceholder.SSN"
                readonly
                :size
                type="text"
            />
        </div>

        <h3 class="mb-4 border-b border-ds-primary-200 pb-2 text-heading-sm">Masked Input Error States</h3>

        <div class="grid grid-cols-3 gap-4">
            <DsTextInputMasked
                v-model="ssnErrorValue"
                error="Social security number is required"
                helperText="Enter your social security number"
                label="Social security number"
                :mask="MaskType.SSN"
                :placeholder="MaskPlaceholder.SSN"
                required
                :size
                type="text"
            />

            <DsTextInputMasked
                v-model="accountErrorValue"
                error="Account number is required"
                helperText="Enter your account number"
                label="Account Number"
                :mask="MaskType.ACCOUNT_NUMBER"
                required
                :size
                type="text"
            />
        </div>

        <h3 class="mb-4 border-b border-ds-primary-200 pb-2 text-heading-sm">Masked Input Warning States</h3>

        <div class="grid grid-cols-3 gap-4">
            <DsTextInputMasked
                v-model="ssnWarningValue"
                label="Social security number"
                :mask="MaskType.SSN"
                :placeholder="MaskPlaceholder.SSN"
                :size
                type="text"
                warning="Double-check this social security number"
            />

            <DsTextInputMasked
                v-model="accountWarningValue"
                label="Account Number"
                :mask="MaskType.ACCOUNT_NUMBER"
                :size
                type="text"
                warning="Double-check this account number"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import DsIcon from '@/components/design-system/DsIcon.vue';
import DsRadio from '@ds/form/input/DsRadio.vue';
import DsTextInput from '@ds/form/input/text/DsTextInput.vue';
import DsTextInputMasked from '@ds/form/input/text/DsTextInputMasked.vue';
import { type DsInputSize, MaskPlaceholder, MaskType } from '@ds/form/input/text/text-input.types';
import { ref } from 'vue';

const size = ref<DsInputSize>('md');

const basicValue = ref('Text sample');
const helperValue = ref('Text sample');
const customTooltipValue = ref('Text sample');
const requiredValue = ref('Text sample');
const disabledValue = ref('Text sample');
const readonlyValue = ref('Text sample');
const labelSlotValue = ref('Text sample');
const maxlengthValue = ref('Text sample');
const errorValue = ref('Text sample');
const errorBooleanValue = ref('Text sample');
const warningValue = ref('Text sample');
const errorWinsValue = ref('Text sample');
const slotsValue = ref('Text sample');
const helperSlotValue = ref('Text sample');

const emailValue = ref('user@example.com');
const passwordValue = ref('password123');
const numberValue = ref(25);

const ssnValue = ref('123456789');
const einValue = ref('123456789');
const accountValue = ref('123456789');
const ssnDisabledValue = ref('123456789');
const ssnReadonlyValue = ref('123456789');

const ssnErrorValue = ref('');
const accountErrorValue = ref('');

const ssnWarningValue = ref('123456789');
const accountWarningValue = ref('123456789');
</script>
